/**
 * Centralized language definitions for the AI Translations application
 * Organized by region for better UX in grouped dropdowns
 */

export const LANGUAGE_REGIONS = {
  WESTERN_EUROPE: 'Western Europe',
  EASTERN_EUROPE: 'Eastern Europe',
  ASIA: 'Asia',
  MIDDLE_EAST: 'Middle East',
  NORDIC: 'Nordic',
  OTHER_EUROPE: 'Other European',
  SOUTH_ASIA: 'South Asia',
  AFRICA: 'Africa'
};

export const LANGUAGES = [
  // Western European Languages
  { code: 'en', name: 'English', region: LANGUAGE_REGIONS.WESTERN_EUROPE },
  { code: 'fr', name: 'French', region: LANGUAGE_REGIONS.WESTERN_EUROPE },
  { code: 'es', name: 'Spanish', region: LANGUAGE_REGIONS.WESTERN_EUROPE },
  { code: 'de', name: 'German', region: LANGUAGE_REGIONS.WESTERN_EUROPE },
  { code: 'it', name: 'Italian', region: LANGUAGE_REGIONS.WESTERN_EUROPE },
  { code: 'pt', name: 'Portuguese', region: LANGUAGE_REGIONS.WESTERN_EUROPE },
  { code: 'nl', name: 'Dutch', region: LANGUAGE_REGIONS.WESTERN_EUROPE },
  { code: 'sv', name: 'Swedish', region: LANGUAGE_REGIONS.WESTERN_EUROPE },
  { code: 'no', name: 'Norwegian', region: LANGUAGE_REGIONS.WESTERN_EUROPE },

  // Eastern European Languages
  { code: 'ru', name: 'Russian', region: LANGUAGE_REGIONS.EASTERN_EUROPE },
  { code: 'pl', name: 'Polish', region: LANGUAGE_REGIONS.EASTERN_EUROPE },
  { code: 'cs', name: 'Czech', region: LANGUAGE_REGIONS.EASTERN_EUROPE },
  { code: 'uk', name: 'Ukrainian', region: LANGUAGE_REGIONS.EASTERN_EUROPE },

  // Asian Languages
  { code: 'zh-CN', name: 'Chinese (Simplified)', region: LANGUAGE_REGIONS.ASIA },
  { code: 'zh-TW', name: 'Chinese (Traditional)', region: LANGUAGE_REGIONS.ASIA },
  { code: 'ja', name: 'Japanese', region: LANGUAGE_REGIONS.ASIA },
  { code: 'ko', name: 'Korean', region: LANGUAGE_REGIONS.ASIA },
  { code: 'hi', name: 'Hindi', region: LANGUAGE_REGIONS.ASIA },
  { code: 'th', name: 'Thai', region: LANGUAGE_REGIONS.ASIA },
  { code: 'vi', name: 'Vietnamese', region: LANGUAGE_REGIONS.ASIA },
  { code: 'id', name: 'Indonesian', region: LANGUAGE_REGIONS.ASIA },
  { code: 'ms', name: 'Malay', region: LANGUAGE_REGIONS.ASIA },
  { code: 'fil', name: 'Filipino', region: LANGUAGE_REGIONS.ASIA },

  // Middle Eastern Languages
  { code: 'ar', name: 'Arabic', region: LANGUAGE_REGIONS.MIDDLE_EAST },
  { code: 'he', name: 'Hebrew', region: LANGUAGE_REGIONS.MIDDLE_EAST },
  { code: 'tr', name: 'Turkish', region: LANGUAGE_REGIONS.MIDDLE_EAST },
  { code: 'fa', name: 'Persian (Farsi)', region: LANGUAGE_REGIONS.MIDDLE_EAST },

  // Nordic Languages
  { code: 'da', name: 'Danish', region: LANGUAGE_REGIONS.NORDIC },
  { code: 'fi', name: 'Finnish', region: LANGUAGE_REGIONS.NORDIC },

  // Other European Languages
  { code: 'el', name: 'Greek', region: LANGUAGE_REGIONS.OTHER_EUROPE },
  { code: 'hu', name: 'Hungarian', region: LANGUAGE_REGIONS.OTHER_EUROPE },
  { code: 'ro', name: 'Romanian', region: LANGUAGE_REGIONS.OTHER_EUROPE },

  // South Asian Languages
  { code: 'bn', name: 'Bengali', region: LANGUAGE_REGIONS.SOUTH_ASIA },
  { code: 'ur', name: 'Urdu', region: LANGUAGE_REGIONS.SOUTH_ASIA },

  // African Languages
  { code: 'sw', name: 'Swahili', region: LANGUAGE_REGIONS.AFRICA }
];

/**
 * Helper function to get languages grouped by region
 * @returns {Object} Languages grouped by region
 */
export const getLanguagesByRegion = () => {
  return LANGUAGES.reduce((acc, lang) => {
    if (!acc[lang.region]) {
      acc[lang.region] = [];
    }
    acc[lang.region].push(lang);
    return acc;
  }, {});
};

/**
 * Helper function to get language name by code
 * @param {string} code - Language code
 * @returns {string} Language name or code if not found
 */
export const getLanguageName = (code) => {
  const language = LANGUAGES.find(lang => lang.code === code);
  return language ? language.name : code;
};

/**
 * Helper function to validate language code
 * @param {string} code - Language code to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidLanguageCode = (code) => {
  return LANGUAGES.some(lang => lang.code === code);
};

/**
 * Get flat list of language options for simple dropdowns
 * @returns {Array} Array of {value, label} objects
 */
export const getLanguageOptions = () => {
  return LANGUAGES.map(lang => ({
    value: lang.code,
    label: lang.name
  }));
};
